import React, { useState, useEffect, useCallback } from "react";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  deleteDoc,
  doc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { getAuth } from "firebase/auth";

const firestore = getFirestore();
const storage = getStorage();

export default function FileUpload({
  taskId,
  onUpload, // (taskId, newFiles[])  → callback del padre
  onDeleteAttachment, // (taskId, fileId)
  existingAttachments = [], // array con los adjuntos ya guardados en la tarea
}) {
  /* ------------------------------ state ---------------------------- */
  const [fileInfo, setFileInfo] = useState([]); // archivos listados desde la colección "files"
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // 0‒100
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  /* ------------------ cargar archivos existentes ------------------ */
  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        if (taskId) {
          const q = query(
            collection(firestore, "files"),
            where("taskId", "==", taskId)
          );
          const snap = await getDocs(q);
          if (!mounted) return;
          const files = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setFileInfo(files);
        }
      } catch (err) {
        console.error("loadFiles error:", err);
        setError("Error al cargar archivos");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [taskId]);

  /* -------------- subir archivo a Firebase Storage --------------- */
  const uploadFileToStorage = (file) =>
    new Promise((resolve, reject) => {
      // usa nombre único -> fecha + nombre original
      const uniqueName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `carpeta_archivos/${uniqueName}`);

      const task = uploadBytesResumable(storageRef, file);

      task.on(
        "state_changed",
        (snap) =>
          setProgress(
            Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
          ),
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve({ url, name: file.name, path: storageRef.fullPath });
          } catch (err) {
            reject(err);
          }
        }
      );
    });

  /* --------------------- handleUpload ----------------------------- */
  const handleUpload = useCallback(
    async (file) => {
      if (!file || !taskId) return;

      // valida tamaño 5 MB
      if (file.size > 5 * 1024 * 1024) {
        return setError("El archivo excede 5 MB");
      }

      setError("");
      setUploading(true);
      try {
        const { url, name, path } = await uploadFileToStorage(file);

        /* 👇 NUEVO: ownerId = UID del usuario logeado */
        const ownerId = getAuth().currentUser.uid;

        // guarda doc en Firestore
        const docRef = await addDoc(collection(firestore, "files"), {
          url,
          name,
          storagePath: path,
          taskId,
          ownerId, // ← añadido
          createdAt: serverTimestamp(),
        });

        const newFile = { id: docRef.id, url, name };
        setFileInfo((prev) => [...prev, newFile]);
        onUpload?.(taskId, [newFile]);
      } catch (err) {
        console.error("upload error:", err);
        setError(err.message || "Error al subir el archivo");
        Swal.fire({
          title: "Error al subir",
          text: err.message,
          icon: "error",
        });
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [taskId, onUpload]
  );

  /* ------------------ DropZone callbacks ------------------------- */
  const onDrop = (accepted) => {
    if (!accepted.length) return;

    // elimina duplicados en existingAttachments y en fileInfo actual
    const currentNames = [...existingAttachments, ...fileInfo].map(
      (f) => f.name
    );
    const toUpload = accepted.filter((f) => !currentNames.includes(f.name));
    const duplicates = accepted.filter((f) => currentNames.includes(f.name));

    if (duplicates.length) {
      Swal.fire({
        title: "Duplicados omitidos",
        text: duplicates.map((d) => d.name).join(", "),
        icon: "warning",
      });
    }

    toUpload.forEach(handleUpload);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  /* ------------------- acciones por archivo ---------------------- */
  const handleDownload = (url, name) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (id, storagePath) => {
    try {
      const { isConfirmed } = await Swal.fire({
        title: "¿Eliminar archivo?",
        icon: "warning",
        showCancelButton: true,
      });
      if (!isConfirmed) return;

      // 1. Borra de Storage
      if (storagePath) {
        await deleteObject(ref(storage, storagePath));
      }

      // 2. Borra doc Firestore
      await deleteDoc(doc(firestore, "files", id));

      // 3. Estado local + callback tarea
      setFileInfo((prev) => prev.filter((f) => f.id !== id));
      onDeleteAttachment?.(taskId, id);
    } catch (err) {
      console.error("delete file error:", err);
      Swal.fire({ title: "Error", text: err.message, icon: "error" });
    }
  };

  /* ------------------------- render ------------------------------ */
  return (
    <div style={{ maxWidth: 600, margin: "auto" }}>
      <h4 style={{ fontWeight: 400, color: "#757575" }}>Cargar un archivo</h4>
      <div {...getRootProps()} className="dropzone">
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Suelta el archivo aquí…</p>
        ) : (
          <>
            <p>
              Arrastra y suelta un archivo aquí, o haz clic para seleccionar uno
            </p>
            <p style={{ color: "#007bff" }}>(Tamaño máximo: 5 MB)</p>
          </>
        )}
      </div>

      {uploading && (
        <div className="progress-container" style={{ marginTop: 10 }}>
          <progress value={progress} max={100} />
          <span style={{ marginLeft: 10 }}>{progress}%</span>
        </div>
      )}

      {loading ? (
        <p>Cargando…</p>
      ) : fileInfo.length === 0 ? (
        <p>No hay archivos</p>
      ) : (
        fileInfo.map((f) => (
          <div key={f.id} className="file-info">
            <div className="file-container">
              <div className="file-image">
                <img src="/img/file.svg" alt="archivo" />
              </div>
              <div className="file-content">
                <p style={{ fontSize: 14, marginBottom: 10 }}>
                  Archivo: {f.name}
                </p>
                <div className="file-actions">
                  <button onClick={() => handleDownload(f.url, f.name)}>
                    Descargar
                  </button>
                  <button onClick={() => handleDelete(f.id, f.storagePath)}>
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
}
